'use client';

import { useLanguage } from '@/context/LanguageContext';
import { ArrowRight } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Reveal from '@/components/Reveal';
import type { BlogPost } from '@/i18n/blog-data';
import type { Locale } from '@/i18n/config';

export default function BlogListClient({
  posts,
  locale,
}: {
  posts: BlogPost[];
  locale: Locale;
}) {
  const { lang, t } = useLanguage();

  return (
    <>
      <Navbar />
      <main className="pt-24 pb-20 bg-dark-950 min-h-screen">
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-sm font-semibold text-electric tracking-widest uppercase mb-4">
              Blog
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
              {t('Ressources & conseils IT', 'IT resources & tips')}
            </h1>
            <p className="text-lg text-white/50 leading-relaxed mb-12 max-w-2xl">
              {t(
                'Articles pratiques pour les PME qui veulent mieux comprendre et gérer leur IT.',
                'Practical articles for SMEs who want to better understand and manage their IT.'
              )}
            </p>
          </Reveal>

          <div className="space-y-8">
            {posts.map((post, i) => {
              const content = post[lang];
              return (
                <Reveal key={post.slug} delay={i * 0.1}>
                  <a
                    href={`/${lang}/blog/${post.slug}`}
                    className="block group rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm p-6 md:p-8 hover:bg-white/[0.05] hover:border-white/[0.12] transition-all duration-500"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <time
                        dateTime={post.date}
                        className="text-sm text-white/30"
                      >
                        {new Date(post.date).toLocaleDateString(
                          lang === 'fr' ? 'fr-FR' : 'en-US',
                          { year: 'numeric', month: 'long', day: 'numeric' }
                        )}
                      </time>
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-white group-hover:text-electric transition-colors duration-300 mb-3">
                      {content.title}
                    </h2>
                    <p className="text-white/40 leading-relaxed mb-4">
                      {content.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-electric">
                      {t('Lire l\u2019article', 'Read article')}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </a>
                </Reveal>
              );
            })}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
